import React, { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { TrendingUp, DollarSign, BarChart3, PieChart as PieIcon, MessageSquare, Star } from "lucide-react";

const PALETTE = {
  primary: "#d4af37",
  approved: "#10b981",
  pending: "#f59e0b",
  rejected: "#ef4444",
  revenue: "#d4af37",
  count: "#7dd3fc",
  bg: "rgba(15,15,18,0.95)",
};
const PIE_COLORS = ["#10b981", "#f59e0b", "#ef4444", "#6b7280", "#a78bfa", "#7dd3fc"];

function fmtMoney(v) {
  if (!v && v !== 0) return "—";
  return new Intl.NumberFormat("ro-RO", { style: "currency", currency: "RON", maximumFractionDigits: 0 }).format(v);
}
function shortDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()}/${d.getMonth() + 1}`;
}
function shortMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const names = ["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Noi","Dec"];
  return `${names[parseInt(m,10)-1]} ${y.slice(2)}`;
}

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="text-xs p-2 rounded shadow" style={{ background: PALETTE.bg, border: "1px solid var(--brand-border)", color: "var(--brand-text)" }}>
      <div className="font-medium mb-1" style={{ color: "var(--brand-primary)" }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || p.fill }}></span>
          <span style={{ color: "var(--brand-text-secondary)" }}>{p.name}:</span>
          <span className="font-mono">{p.name === "Revenue" ? fmtMoney(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function AdvancedAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError("");
    api.get("/admin/analytics/advanced", { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const timelineData = useMemo(() => (data?.timeline || []).map((d) => ({ ...d, dayLabel: shortDay(d.day) })), [data]);
  const revenueMonths = useMemo(() => (data?.revenue_by_month || []).map((m) => ({ ...m, label: shortMonth(m.month) })), [data]);
  const statusData = useMemo(() => {
    const map = { approved: "Aprobate", pending: "In asteptare", rejected: "Respinse", cancelled: "Anulate", completed: "Finalizate" };
    return (data?.status_distribution || []).map((s) => ({ name: map[s.status] || s.status, value: s.count }));
  }, [data]);
  const topCars = useMemo(() => (data?.top_cars_revenue || []).map((c) => ({ ...c, displayName: `${c.brand} ${c.name}`.slice(0, 24) })), [data]);

  if (loading && !data) {
    return <div className="card-surface p-8 text-center text-sm" style={{ color: "var(--brand-text-secondary)" }}>Se incarca analytics...</div>;
  }
  if (error) {
    return <div className="card-surface p-6 text-sm" style={{ color: "#fca5a5" }} data-testid="analytics-error">Eroare: {error}</div>;
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="advanced-analytics">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-2xl">Analytics avansat</h2>
        <div className="flex items-center gap-1 text-xs">
          <span style={{ color: "var(--brand-text-secondary)" }} className="mr-2">Perioada:</span>
          {[14, 30, 60, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 ${days === d ? "btn-gold-filled" : "btn-ghost"}`}
              data-testid={`range-${d}`}
            >
              {d}z
            </button>
          ))}
        </div>
      </div>

      {/* Quick KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Venit aprobate (total)"
          value={fmtMoney((data.timeline || []).reduce((s, x) => s + (x.revenue || 0), 0))}
        />
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label={`Rezervari ultimele ${days}z`}
          value={(data.timeline || []).reduce((s, x) => s + (x.count || 0), 0)}
        />
        <KpiCard
          icon={<MessageSquare className="w-4 h-4" />}
          label="Lead-uri convertite"
          value={`${data.leads?.converted || 0} / ${data.leads?.total || 0}`}
          sub={`${data.leads?.conversion_pct || 0}% conversie`}
        />
        <KpiCard
          icon={<Star className="w-4 h-4" />}
          label="Rating mediu"
          value={data.reviews?.avg_rating ? `${data.reviews.avg_rating} ★` : "—"}
          sub={`din ${data.reviews?.rating_count || 0} recenzii`}
        />
      </div>

      {/* Timeline chart - reservations + revenue */}
      <div className="card-surface p-5" data-testid="chart-timeline">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
          <h3 className="font-medium">Rezervari & venit pe zi ({days} zile)</h3>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={timelineData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
            <defs>
              <linearGradient id="gradCount" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PALETTE.count} stopOpacity={0.55} />
                <stop offset="100%" stopColor={PALETTE.count} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradRev" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={PALETTE.revenue} stopOpacity={0.55} />
                <stop offset="100%" stopColor={PALETTE.revenue} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="dayLabel" stroke="var(--brand-text-secondary)" style={{ fontSize: 10 }} interval={Math.max(0, Math.floor(timelineData.length / 12))} />
            <YAxis yAxisId="left" stroke="var(--brand-text-secondary)" style={{ fontSize: 10 }} allowDecimals={false} />
            <YAxis yAxisId="right" orientation="right" stroke="var(--brand-primary)" style={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
            <Tooltip content={<TooltipBox />} />
            <Legend wrapperStyle={{ fontSize: 11, color: "var(--brand-text-secondary)" }} />
            <Area yAxisId="left" type="monotone" dataKey="count" name="Rezervari" stroke={PALETTE.count} strokeWidth={2} fill="url(#gradCount)" />
            <Area yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke={PALETTE.revenue} strokeWidth={2} fill="url(#gradRev)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Two-up: revenue by month + status pie */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-5" data-testid="chart-revenue-month">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
            <h3 className="font-medium">Venit pe luna (ultimele 12)</h3>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenueMonths} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="label" stroke="var(--brand-text-secondary)" style={{ fontSize: 10 }} />
              <YAxis stroke="var(--brand-text-secondary)" style={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <Tooltip content={<TooltipBox />} />
              <Bar dataKey="revenue" name="Revenue" fill={PALETTE.revenue} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-surface p-5" data-testid="chart-status-pie">
          <div className="flex items-center gap-2 mb-3">
            <PieIcon className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
            <h3 className="font-medium">Distributie status (toate rezervarile)</h3>
          </div>
          {statusData.length === 0 ? (
            <p className="text-sm text-center py-12" style={{ color: "var(--brand-text-secondary)" }}>Nicio rezervare inca.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2}>
                  {statusData.map((_, i) => (<Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />))}
                </Pie>
                <Tooltip content={<TooltipBox />} />
                <Legend wrapperStyle={{ fontSize: 11, color: "var(--brand-text-secondary)" }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Top cars by revenue */}
      <div className="card-surface p-5" data-testid="chart-top-cars-revenue">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
          <h3 className="font-medium">Top masini dupa venit</h3>
        </div>
        {topCars.length === 0 ? (
          <p className="text-sm text-center py-12" style={{ color: "var(--brand-text-secondary)" }}>Nicio rezervare aprobata inca.</p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, topCars.length * 40)}>
            <BarChart data={topCars} layout="vertical" margin={{ top: 4, right: 16, left: 30, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis type="number" stroke="var(--brand-text-secondary)" style={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <YAxis dataKey="displayName" type="category" stroke="var(--brand-text-secondary)" style={{ fontSize: 11 }} width={160} />
              <Tooltip content={<TooltipBox />} />
              <Bar dataKey="revenue" name="Revenue" fill={PALETTE.revenue} radius={[0, 4, 4, 0]} />
              <Bar dataKey="count" name="Rezervari" fill={PALETTE.count} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Lead funnel */}
      <div className="card-surface p-5" data-testid="chart-lead-funnel">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="w-4 h-4" style={{ color: "var(--brand-primary)" }} />
          <h3 className="font-medium">Funnel lead-uri</h3>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center text-sm">
          <FunnelStep label="Lead-uri primite" value={data.leads?.total || 0} color="#7dd3fc" />
          <FunnelStep label="Contactate" value={data.leads?.contacted || 0} color="#f59e0b" />
          <FunnelStep label="Convertite" value={data.leads?.converted || 0} color="#10b981" sub={`${data.leads?.conversion_pct || 0}%`} />
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub }) {
  return (
    <div className="card-surface p-4">
      <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: "var(--brand-text-secondary)" }}>{icon}{label}</div>
      <div className="font-serif text-2xl">{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--brand-text-secondary)" }}>{sub}</div>}
    </div>
  );
}

function FunnelStep({ label, value, sub, color }) {
  return (
    <div className="rounded p-4" style={{ background: "rgba(255,255,255,0.02)", borderTop: `2px solid ${color}` }}>
      <div className="text-xs mb-1" style={{ color: "var(--brand-text-secondary)" }}>{label}</div>
      <div className="font-serif text-2xl" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] mt-0.5" style={{ color }}>{sub}</div>}
    </div>
  );
}
